import equal from 'fast-deep-equal';
import { combineReducers } from 'redux';

import { createMap, modifyArrayItemAt, removeArrayItemAt, removeArrayItemAtIndex } from '../../../utils/arrayUtil';
import { debounceAction } from '../../../utils/debounce';

export const filterTypes = {
  string: 'string',
  date: 'date',
  enum: 'enum',
  number: 'number'
};

export const filterOperators = {
  isEqualTo: 'is equal to',
  contains: 'contains',
  startsWith: 'starts with',
  endsWith: 'ends with',
  isAfter: 'is on or after',
  isBefore: 'is before'
};

/**
 * utility to build the search filters from the state
 */
function buildBoolFilters({
  advancedFilters = [],
  quickFilters = [],
  baseFilters = [],
  globalSearchIndices = [],
  defaultAdvancedFilters = [],
  globalSearchValue
}) {
  const accompanyingFilters = advancedFilters.reduce((filters, filter) => (
    filter?.property?.accompanyingFilters
      ? [...filters, ...filter.property.accompanyingFilters]
      : filters), defaultAdvancedFilters).map(f => ({ property: f, value: f.value }));

  const advancedFiltersWithAccompanying = [...advancedFilters, ...accompanyingFilters];
  const regularAdvancedFilters = advancedFiltersWithAccompanying.filter(f => !f.property.nestedPath);
  const nestedAdvancedFilters = advancedFiltersWithAccompanying.filter(f => !!f.property.nestedPath);
  const regularQuickFilters = quickFilters.filter(f => !f.nestedPath && !!f.selected.length);
  const nestedQuickFilters = quickFilters.filter(f => !!f.nestedPath && !!f.selected.length);
  const nestedAdvancedFilterMap = createMap(nestedAdvancedFilters, 'property.nestedPath', true);
  const nestedQuickFilterMap = createMap(nestedQuickFilters, 'nestedPath', true);

  const nested = Object.keys({ ...nestedAdvancedFilterMap, ...nestedQuickFilterMap }).map(path => {
    const advFilters = (nestedAdvancedFilterMap[path] || [])
      .map(({ property: { nestedPath, ...property }, ...f }) => ({ ...f, property }));

    const qckFilters = (nestedQuickFilterMap[path] || [])
      .map(({ nestedPath, ...quickFilter }) => ({ ...quickFilter }));

    if (advFilters.length || qckFilters.length) {
      return {
        nested: {
          path,
          query: { bool: buildBoolFilters({ advancedFilters: advFilters, quickFilters: qckFilters }) }
        }
      };
    }

    return [];
  });

  const stringMatches = regularAdvancedFilters
    .filter(filter => filter.property.type === filterTypes.string)
    .map(filter => {
      let returnQuery;

      if (filter.shouldQuery && filter.operator === filterOperators.isEqualTo) {
        returnQuery = {
          multi_match: {
            query: filter.value,
            fields: filter.property.keys,
            type: 'bool_prefix',
            operator: 'or'
          }
        };

        return returnQuery;
      }

      switch (filter.operator) {
        case filterOperators.contains:
          returnQuery = {
            query_string: {
              default_field: `${filter.property.key}.keyword`,
              query: `*${filter.value}*`
            }
          };

          break;
        case filterOperators.startsWith:
          returnQuery = {
            query_string: {
              default_field: `${filter.property.key}.keyword`,
              query: `${filter.value}*`
            }
          };

          break;
        case filterOperators.endsWith:
          returnQuery = {
            query_string: {
              default_field: `${filter.property.key}.keyword`,
              query: `*${filter.value}`
            }
          };

          break;
        default:
          returnQuery = { term: { [`${filter.property.key}.keyword`]: filter.value } };
      }

      return returnQuery;
    });

  const dateFilters = regularAdvancedFilters
    .filter(filter => (filter.property.type === filterTypes.date));

  const mappedDateFilters = createMap(dateFilters, 'property.key', true);

  const dateMatches = Object.keys(mappedDateFilters).map(key => {
    const ranges = mappedDateFilters[key];

    return ranges.reduce((accum, filter) => {
      const operator = filter.operator === filterOperators.isBefore ? 'lt' : 'gte';
      return {
        range: {
          [key]: {
            ...(accum?.range?.[key] || {}),
            [operator]: filter.value
          }
        }
      };
    }, {});
  });

  const enumFilters = regularAdvancedFilters
    .filter(filter => filter.property.type === filterTypes.enum)
    .map(filter => {
      if (filter.property.shouldQuery && filter.operator === filterOperators.isEqualTo) {
        return {
          multi_match: {
            query: filter.value,
            fields: filter.property.keys,
            type: 'bool_prefix',
            operator: 'or'
          }
        };
      }

      const key = filter.property.key.endsWith('.id')
        ? filter.property.key
        : `${filter.property.key}.keyword`;

      return ({ term: { [key]: filter.value } });
    });

  const quickFilterMatches = regularQuickFilters
    .map(quickFilter => ({
      bool: {
        minimum_should_match: 1,
        should: quickFilter.selected.map(item => (
          { term: { [quickFilter.searchKey]: item[quickFilter.idKey] } }
        ))
      }
    }));

  const musts = [...stringMatches, ...dateMatches, ...quickFilterMatches, ...enumFilters, ...nested];

  const bool = {
    must: musts,
    filter: [
      ...baseFilters.map(filter => (Array.isArray(filter.value) ? ({ terms: { [filter.key]: filter.value } }) : ({ term: { [filter.key]: filter.value } })))
    ]
  };

  if (globalSearchValue && globalSearchValue?.trim() !== '') {
    bool.must = [
      ...bool.must,
      {
        multi_match: {
          query: globalSearchValue,
          fields: globalSearchIndices,
          type: 'bool_prefix',
          operator: 'and'
        }
      }
    ];
  }

  return bool;
}

export const withSearchBlueprint = (name, baseReducer, {
  endpoint,
  defaultSort = [],
  scriptScore = '',
  pageSize = 20,
  dataTransformer,
  globalSearchIndices: defaultGlobalSearchIndices = [],
  lengthAwarePagination = true,
  defaultAdvancedFilters = [],
  aggregates = false
} = {}) => {
  const SEARCH_ADD_ADVANCED_FILTER = 'SEARCH_ADD_ADVANCED_FILTER';
  const SEARCH_SET_ADVANCED_FILTERS = 'SEARCH_SET_ADVANCED_FILTERS';
  const SEARCH_APPLY_FILTERS = 'SEARCH_APPLY_FILTERS';

  const SEARCH_GET_SEARCH_PAGE = 'SEARCH_GET_SEARCH_PAGE';
  const SEARCH_GET_SEARCH_PAGE_SUCCESS = 'SEARCH_GET_SEARCH_PAGE_SUCCESS';
  const SEARCH_GET_SEARCH_PAGE_FAILURE = 'SEARCH_GET_SEARCH_PAGE_FAILURE';

  const SEARCH_APPEND_ITEM = 'SEARCH_APPEND_ITEM';
  const SEARCH_PREPEND_ITEM = 'SEARCH_PREPEND_ITEM';

  const SEARCH_REMOVE_ITEM = 'SEARCH_REMOVE_ITEM';

  const SEARCH_SET_BASE_FILTERS = 'SEARCH_SET_BASE_FILTERS';
  const SEARCH_SET_COLUMNS = 'SEARCH_SET_COLUMNS';
  const SEARCH_SET_HIDDEN_COLUMN_IDS = 'SEARCH_SET_HIDDEN_COLUMN_IDS';
  const SEARCH_SET_GLOBAL_SEARCH_INDICES = 'SEARCH_SET_GLOBAL_SEARCH_INDICES';
  const SEARCH_SET_QUICK_FILTER_OPTIONS = 'SEARCH_SET_QUICK_FILTERS';
  const SEARCH_SET_ADVANCED_FILTER_OPTIONS = 'SEARCH_SET_ADVANCED_FILTER_OPTIONS';
  const SEARCH_SET_DEFAULT_ADVANCED_FILTERS = 'SEARCH_SET_DEFAULT_ADVANCED_FILTERS';
  const SEARCH_SET_SCRIPT_SCORE = 'SEARCH_SET_SCRIPT_SCORE';
  const SEARCH_SET_POST_CREATE_FILTER = 'SEARCH_SET_POST_CREATE_FILTER';

  const SEARCH_UPDATE_GLOBAL_SEARCH_VALUE = 'SEARCH_UPDATE_GLOBAL_SEARCH_VALUE';
  const SEARCH_UPDATE_ITEM = 'SEARCH_UPDATE_ITEM';
  const SEARCH_UPDATE_ITEMS = 'SEARCH_UPDATE_ITEMS';
  const SEARCH_UPDATE_STAGED_FILTER = 'SEARCH_UPDATE_STAGED_FILTER';
  const SEARCH_UPDATE_QUICK_FILTERS = 'SEARCH_UPDATE_QUICK_FILTERS';
  const SEARCH_UPDATE_SORT_BY = 'SEARCH_UPDATE_SORT_BY';

  const SEARCH_REMOVE_ADVANCED_FILTER = 'SEARCH_REMOVE_ADVANCED_FILTER';
  const SEARCH_RESET_FILTERS = 'SEARCH_RESET_FILTERS';
  const SEARCH_TOGGLE_COLUMN = 'SEARCH_TOGGLE_COLUMN';

  const SEARCH_SET_LAST_INDEX = 'SEARCH_SET_LAST_INDEX';

  const initialState = {
    isLoading: true,
    sortBy: defaultSort,
    baseFilters: [],
    postCreateFilter: null,
    quickFilters: [],
    advancedFilterOptions: [],
    advancedFilters: [],
    stagedFilter: {
      name: null,
      key: null,
      type: filterTypes.enum,
      operators: [
        filterOperators.isEqualTo
      ],
      values: []
    },
    data: [],
    pageInfo: {},
    filters: {},
    selectedPresets: [],
    allColumns: [],
    columns: [],
    hiddenColumnIds: [],
    columnsToggled: false,
    globalSearchValue: '',
    globalSearchIndices: defaultGlobalSearchIndices,
    lengthAwarePagination,
    defaultAdvancedFilters
  };

  /* Actions
  ========================================================= */
  const getPage = debounceAction(({ page = 1, after = null } = {}) => async (dispatch, getState) => {
    const isNewPage = lengthAwarePagination ? page > 1 : !!after;
    dispatch({ name, type: SEARCH_GET_SEARCH_PAGE, isNewPage });

    // If something changes about the search, we need to reset the after
    // index to 0 and restart the pagination
    const pageData = lengthAwarePagination
      ? { pageSize, page }
      : { pageSize, after };

    // Get the post create filter if it exists, this filter is used to prepend newly created
    // items to the beginning of the data array, typically after creation.
    const postCreateFilter = selectPostCreateFilter(getState());

    const requestParams = getSearchRequestParams(getState(), { sortOverride: !!postCreateFilter && [{ id: postCreateFilter, desc: true }] });

    try {
      const { data, ...pageInfo } = await endpoint(requestParams, { ...pageData, aggregates });

      dispatch({ name, camelCase: false, type: SEARCH_SET_LAST_INDEX, lastIndex: pageInfo.last_index });

      dispatch({
        name,
        type: SEARCH_GET_SEARCH_PAGE_SUCCESS,
        data: dataTransformer ? data.map(dataTransformer) : data,
        isNewPage,
        pageInfo
      });
    } catch (error) {
      dispatch({ name, type: SEARCH_GET_SEARCH_PAGE_FAILURE, error });
    }
  });

  const SEARCH_CLEAR_SEARCH = 'SEARCH_CLEAR_SEARCH';

  const clearSearch = () => dispatch => {
    dispatch({ name, type: SEARCH_CLEAR_SEARCH });
  };

  const updateGlobalSearchFilter = debounceAction(value => dispatch => {
    dispatch({ name, type: SEARCH_UPDATE_GLOBAL_SEARCH_VALUE, value });
    dispatch(getPage());
  });

  /**
     * addAdvancedFilter
     *
     * @returns {function(*): void}
     */
  const addAdvancedFilter = () => dispatch => {
    dispatch({ name, type: SEARCH_ADD_ADVANCED_FILTER });
  };

  /**
     * setAdvancedFilters
     *
     * add multiple advanced filters at once
     *
     * @returns {function(*): void}
     */
  const setAdvancedFilters = advancedFilters => dispatch => {
    dispatch({ name, type: SEARCH_SET_ADVANCED_FILTERS, advancedFilters });
    dispatch(getPage());
  };

  /**
     * setPostCreateFilter
     *
     * specify that the next request should be ordered by created_at desc
     *
     * @returns {function(*): void}
     */
  const setPostCreateFilter = (key = 'created_at') => dispatch => {
    dispatch({ name, type: SEARCH_SET_POST_CREATE_FILTER, key });
  };

  /**
     * setDefaultAdvancedFilters
     *
     * add behind-the-scenes advanced filters
     *
     * @returns {function(*): void}
     */
  const setDefaultAdvancedFilters = filters => dispatch => {
    dispatch({ name, type: SEARCH_SET_DEFAULT_ADVANCED_FILTERS, defaultAdvancedFilters: filters });
  };

  /**
     * applyFilters
     *
     * @returns {function(*): void}
     */
  const applyFilters = () => dispatch => {
    dispatch({ name, type: SEARCH_APPLY_FILTERS });
    dispatch(getPage());
  };

  /**
     * handleStateChange
     *
     * @param event
     * @returns {function(*): void}
     */
  const handleStateChange = event => (dispatch, getState) => {
    if (event.sortBy && !selectGlobalSearchValue(getState())) {
      if (!equal(selectSortBy(getState()), event.sortBy)) {
        dispatch(updateSortBy(event.sortBy));
      }
    }
  };

  /**
     * removeAdvancedFilter
     *
     * @param index
     * @returns {function(*): void}
     */
  const removeAdvancedFilter = index => dispatch => {
    dispatch({ name, type: SEARCH_REMOVE_ADVANCED_FILTER, index });
  };

  /**
     * resetFilters
     *
     * @returns {function(*): void}
     */
  const resetFilters = () => dispatch => {
    dispatch({ name, type: SEARCH_RESET_FILTERS });
    dispatch(getPage());
  };

  /**
     * setAllColumns
     *
     * @returns {function(*): void}
     */
  const setHiddenColumnIds = columnIds => dispatch => {
    dispatch({ name, type: SEARCH_SET_HIDDEN_COLUMN_IDS, columnIds });
  };

  /**
     * setColumns
     *
     * @param columns
     * @returns {function(*): void}
     */
  const setColumns = columns => dispatch => {
    dispatch({ name, type: SEARCH_SET_COLUMNS, columns });
  };

  /**
     * toggleColumn
     *
     * @param columnId
     * @returns {function(*): void}
     */
  const toggleColumn = columnId => dispatch => {
    dispatch({ name, type: SEARCH_TOGGLE_COLUMN, columnId });
  };

  /**
     * setBaseFilters
     *
     * @param filters
     * @returns {function(*): void}
     */
  const setBaseFilters = filters => dispatch => {
    dispatch({ name, type: SEARCH_SET_BASE_FILTERS, filters });
  };

  /**
     * setGlobalSearchIndicies
     *
     * @param indices
     * @returns {function(*): void}
     */
  const setGlobalSearchIndicies = indices => dispatch => {
    dispatch({ name, type: SEARCH_SET_GLOBAL_SEARCH_INDICES, indices });
  };

  /**
     * updateItem
     *
     * @param identifier string|function
     * @param identifierValue string|int|function
     * @param updateFn function|null
     * @returns {function(*): void}
     */
  const updateItem = (identifier, identifierValue, updateFn) => dispatch => {
    dispatch({ name, type: SEARCH_UPDATE_ITEM, identifier, identifierValue, updateFn });
  };

  /**
     * updateItems
     *
     * update multiple items at once with an array of update objects:
     *
     * @param updates object
     * @returns {function(*): void}
     */
  const updateItems = updates => dispatch => {
    dispatch({ name, type: SEARCH_UPDATE_ITEMS, updates });
  };

  /**
     * appendItem
     *
     * @param item
     * @returns {function(*): void}
     */
  const appendItem = item => dispatch => {
    dispatch({ name, type: SEARCH_APPEND_ITEM, item });
  };

  /**
     * prependItem
     *
     * @param item
     * @returns {function(*): void}
     */
  const prependItem = item => dispatch => {
    dispatch({ name, type: SEARCH_PREPEND_ITEM, item });
  };

  /**
     * removeItem
     *
     * @param key
     * @param value
     * @returns {(function(*): void)|*}
     */
  const removeItem = (key, value) => dispatch => {
    dispatch({ name, type: SEARCH_REMOVE_ITEM, key, value });
  };

  /**
     * setScriptScore
     *
     * @param score
     * @returns {function(*): void}
     */
  const setScriptScore = score => dispatch => {
    dispatch({ name, type: SEARCH_SET_SCRIPT_SCORE, score });
    dispatch(getPage());
  };

  /**
     * updateQuickFilters
     *
     * @param filterName
     * @param selected
     * @returns {function(*): void}
     */
  const updateQuickFilters = (filterName, selected) => dispatch => {
    dispatch({ name, type: SEARCH_UPDATE_QUICK_FILTERS, filterName, selected });
    dispatch(getPage());
  };

  /**
     * updateSortBy
     *
     * @param sortBy
     * @returns {function(*): void}
     */
  const updateSortBy = sortBy => dispatch => {
    dispatch({ camelCase: false, name, type: SEARCH_UPDATE_SORT_BY, sortBy });
    dispatch(getPage());
  };

  /**
     * updateStagedFilterProperty
     *
     * @param rootFilter
     * @param subProperty
     * @returns {(function(*, *): void)|*}
     */
  const updateStagedFilterProperty = (rootFilter, subProperty) => (dispatch, getState) => {
    const option = selectAdvancedFilterOptions(getState()).find(opt => opt.name === rootFilter);
    const property = option.subProperties.find(p => p.name === subProperty);

    dispatch({
      name,
      type: SEARCH_UPDATE_STAGED_FILTER,
      update: {
        property,
        operator: property.operators[0]
      }
    });
  };

  /**
     * updateStagedFilter
     *
     * @param update
     * @returns {function(*): void}
     */
  const updateStagedFilter = update => dispatch => {
    dispatch({ name, type: SEARCH_UPDATE_STAGED_FILTER, update });
  };

  /**
     * setQuickFilterOptions
     *
     * set the quick filters options
     *
     * @type {function(*): void}
     */
  const setQuickFilterOptions = quickFilters => dispatch => {
    dispatch({ name, type: SEARCH_SET_QUICK_FILTER_OPTIONS, quickFilters });
  };

  /**
     * setAdvancedFilterOptions
     *
     * set the advanced filter options
     *
     * @param advancedFilterOptions
     * @returns {function(*): void}
     */
  const setAdvancedFilterOptions = advancedFilterOptions => dispatch => {
    dispatch({ name, type: SEARCH_SET_ADVANCED_FILTER_OPTIONS, advancedFilterOptions });
  };

  /* Reducer
  ========================================================= */
  const searchReducer = (state = initialState, action) => {
    // This is where the magic happens. It allows us to use the same action types
    // across multiple similar reducers. All actions need a name attached to them.
    if (action.name !== name) return state;

    switch (action.type) {
      case SEARCH_APPEND_ITEM: {
        return {
          ...state,
          data: [...state.data, action.item]
        };
      }

      case SEARCH_PREPEND_ITEM: {
        return {
          ...state,
          data: [action.item, ...state.data]
        };
      }

      case SEARCH_ADD_ADVANCED_FILTER: {
        return {
          ...state,
          advancedFilters: [...state.advancedFilters, state.stagedFilter],
          stagedFilter: initialState.stagedFilter
        };
      }

      case SEARCH_REMOVE_ADVANCED_FILTER: {
        return {
          ...state,
          advancedFilters: [
            ...state.advancedFilters.slice(0, action.index),
            ...state.advancedFilters.slice(action.index + 1)
          ]
        };
      }

      case SEARCH_SET_ADVANCED_FILTERS: {
        return {
          ...state,
          isLoading: true,
          advancedFilters: action.advancedFilters
        };
      }

      case SEARCH_SET_POST_CREATE_FILTER: {
        return {
          ...state,
          isLoading: true,
          postCreateFilter: action.key
        };
      }

      case SEARCH_SET_DEFAULT_ADVANCED_FILTERS: {
        return {
          ...state,
          defaultAdvancedFilters: Array.isArray(action.defaultAdvancedFilters)
            ? action.defaultAdvancedFilters
            : [...state.defaultAdvancedFilters, action.defaultAdvancedFilters]
        };
      }

      case SEARCH_APPLY_FILTERS: {
        let { advancedFilters } = state;

        if (state.stagedFilter.value) {
          advancedFilters = [...advancedFilters, state.stagedFilter];
        }

        return {
          ...state,
          advancedFilters,
          stagedFilter: initialState.stagedFilter
        };
      }

      case SEARCH_GET_SEARCH_PAGE: {
        if (action.isNewPage) {
          return state;
        }

        return {
          ...state,
          isLoading: true
        };
      }

      case SEARCH_CLEAR_SEARCH: {
        return {
          ...initialState,
          hiddenColumnIds: state.hiddenColumnIds || [],
          columnsToggled: state.columnsToggled
        };
      }

      case SEARCH_RESET_FILTERS: {
        return {
          ...state,
          isLoading: true,
          globalSearchValue: '',
          stagedFilter: initialState.stagedFilter,
          advancedFilters: initialState.advancedFilters,
          quickFilters: state.quickFilters.map(quickFilter => ({ ...quickFilter, selected: [] })),
          postCreateFilter: null
        };
      }

      case SEARCH_SET_HIDDEN_COLUMN_IDS: {
        return {
          ...state,
          hiddenColumnIds: action.columnIds
        };
      }

      case SEARCH_SET_COLUMNS: {
        return {
          ...state,
          columns: action.columns
        };
      }

      case SEARCH_SET_BASE_FILTERS: {
        return {
          ...state,
          baseFilters: action.filters
        };
      }

      case SEARCH_SET_SCRIPT_SCORE: {
        return {
          ...state,
          scriptScore: action.score
        };
      }

      case SEARCH_GET_SEARCH_PAGE_SUCCESS: {
        if (action.isNewPage) {
          return {
            ...state,
            data: [...state.data, ...action.data],
            pageInfo: action.pageInfo,
            isLoading: false,
            postCreateFilter: null
          };
        }

        return {
          ...state,
          data: action.data,
          pageInfo: action.pageInfo,
          isLoading: false,
          postCreateFilter: null
        };
      }

      case SEARCH_SET_LAST_INDEX: {
        return { ...state, lastIndex: action.lastIndex };
      }

      case SEARCH_REMOVE_ITEM: {
        return {
          ...state,
          data: removeArrayItemAt(
            state.data,
            action.key,
            action.value
          )
        };
      }

      case SEARCH_SET_GLOBAL_SEARCH_INDICES: {
        return {
          ...state,
          globalSearchIndices: action.indices
        };
      }

      case SEARCH_SET_ADVANCED_FILTER_OPTIONS: {
        return {
          ...state,
          advancedFilterOptions: action.advancedFilterOptions
        };
      }

      case SEARCH_SET_QUICK_FILTER_OPTIONS: {
        return {
          ...state,
          quickFilters: action.quickFilters
        };
      }

      case SEARCH_UPDATE_GLOBAL_SEARCH_VALUE: {
        if (action.value) {
          return {
            ...state,
            globalSearchValue: action.value,
            sortBy: state.sortBy ?? []
          };
        }

        return {
          ...state,
          globalSearchValue: null,
          sortBy: state.sortBy ?? defaultSort
        };
      }

      case SEARCH_UPDATE_ITEM: {
        return {
          ...state,
          data: modifyArrayItemAt(
            state.data,
            action.identifier,
            action.identifierValue,
            action.updateFn
          )
        };
      }

      case SEARCH_UPDATE_ITEMS: {
        const updatedData = action.updates.reduce((data, {
          identifier,
          identifierValue,
          updateFn
        }) => modifyArrayItemAt(
          data,
          identifier,
          identifierValue || updateFn,
          updateFn
        ), state.data);

        return {
          ...state,
          data: updatedData
        };
      }

      case SEARCH_UPDATE_SORT_BY: {
        return {
          ...state,
          sortBy: action.sortBy
        };
      }

      case SEARCH_UPDATE_STAGED_FILTER: {
        return {
          ...state,
          stagedFilter: { ...state.stagedFilter, ...action.update }
        };
      }

      case SEARCH_UPDATE_QUICK_FILTERS: {
        return {
          ...state,
          quickFilters: modifyArrayItemAt(
            state.quickFilters,
            'name',
            action.filterName,
            filter => ({ ...filter, selected: action.selected })
          )
        };
      }

      case SEARCH_TOGGLE_COLUMN: {
        const currentIndex = state.hiddenColumnIds.indexOf(action.columnId);

        if (currentIndex >= 0) {
          return {
            ...state,
            hiddenColumnIds: removeArrayItemAtIndex(state.hiddenColumnIds, currentIndex),
            columnsToggled: true
          };
        }

        return {
          ...state,
          hiddenColumnIds: [...state.hiddenColumnIds, action.columnId],
          columnsToggled: true
        };
      }

      default:
        return state;
    }
  };

  const reducer = baseReducer
    ? combineReducers({ base: baseReducer, search: searchReducer })
    : searchReducer;

  const baseSelector = baseReducer
    ? state => state[name].search
    : state => state[name];

  /* Selectors
  ========================================================= */
  const selectData = state => baseSelector(state).data;
  const selectPageInfo = state => baseSelector(state).pageInfo;
  const selectLastIndex = state => baseSelector(state).lastIndex;
  const selectColumns = state => baseSelector(state).columns;
  const selectBoolFilter = state => buildBoolFilters(baseSelector(state));
  const selectIsLoading = state => baseSelector(state).isLoading;
  const selectPostCreateFilter = state => baseSelector(state).postCreateFilter;
  const selectQuickFilterOptions = state => baseSelector(state).quickFilters;
  const selectAdvancedFilterOptions = state => baseSelector(state).advancedFilterOptions;
  const selectAdvancedFilters = state => baseSelector(state).advancedFilters;
  const selectStagedFilter = state => baseSelector(state).stagedFilter;
  const selectSortBy = state => baseSelector(state).sortBy;
  const selectIsLengthAwarePagination = state => baseSelector(state).lengthAwarePagination;
  const selectColumnIds = state => baseSelector(state).columns.map(c => (c.id || c.accessor));
  const selectHiddenColumnIds = state => baseSelector(state).hiddenColumnIds;
  const selectColumnsToggled = state => baseSelector(state).columnsToggled;
  const selectGlobalSearchValue = state => baseSelector(state).globalSearchValue;
  const selectRequestParamsFunction = state => options => getSearchRequestParams(state, options);

  const selectAppliedFilterCount = state => {
    const { quickFilters, advancedFilters } = baseSelector(state);

    const quickFilterCount = quickFilters.reduce((count, filter) => {
      const { selected } = filter;

      if (selected.length) {
        return count + selected.length;
      }

      return count;
    }, 0);

    return quickFilterCount + advancedFilters.length;
  };

  return {
    addAdvancedFilter,
    appendItem,
    applyFilters,
    clearSearch,
    getPage,
    handleStateChange,
    initialState,
    prependItem,
    reducer,
    removeAdvancedFilter,
    removeItem,
    resetFilters,
    selectAdvancedFilterOptions,
    selectAdvancedFilters,
    selectBoolFilter,
    selectColumnIds,
    selectColumns,
    selectData,
    selectGlobalSearchValue,
    selectHiddenColumnIds,
    selectColumnsToggled,
    selectPostCreateFilter,
    selectIsLengthAwarePagination,
    selectIsLoading,
    selectLastIndex,
    selectPageInfo,
    selectQuickFilterOptions,
    selectRequestParamsFunction,
    selectSortBy,
    selectStagedFilter,
    selectAppliedFilterCount,
    setAdvancedFilterOptions,
    setAdvancedFilters,
    setBaseFilters,
    setColumns,
    setDefaultAdvancedFilters,
    setGlobalSearchIndicies,
    setHiddenColumnIds,
    setPostCreateFilter,
    setQuickFilterOptions,
    setScriptScore,
    toggleColumn,
    updateGlobalSearchFilter,
    updateItem,
    updateItems,
    updateQuickFilters,
    updateSortBy,
    updateStagedFilter,
    updateStagedFilterProperty
  };

  /* Utils
  ================================================== */
  function getSearchRequestParams(state, options = {}) {
    const {
      sortBy,
      columns
    } = baseSelector(state);

    const {
      skipScore,
      filterOverride,
      sortOverride
    } = options;

    const sort = sortOverride
      ? sortOverride.map(({ id, desc }) => ({ [id]: { order: desc ? 'desc' : 'asc' } }))
      : sortBy.map(({ id, desc }) => {
        const column = columns.find(({ id: columnId, accessor }) => columnId === id || accessor === id);
        if (!column) return null;
        return { [column.sortKey || id]: { order: desc ? 'desc' : 'asc', unmapped_type: 'keyword' } };
      }).filter(Boolean);

    const source = sortOverride ? null : sortBy.map(({ id, desc }) => {
      const column = columns.find(({ id: columnId, accessor }) => columnId === id || accessor === id);
      if (!column) return null;
      return { [column?.sortName || id]: { terms: { field: column?.sortKey || id, order: desc ? 'desc' : 'asc' } } };
    }).filter(Boolean);

    const scriptScoreQuery = {
      function_score: {
        boost_mode: 'sum',
        query: { bool: buildBoolFilters(baseSelector(state)) },
        functions: [
          { script_score: { script: scriptScore } }
        ]
      }
    };

    const query = scriptScore && !skipScore && !filterOverride
      ? scriptScoreQuery
      : filterOverride || { bool: buildBoolFilters(baseSelector(state)) };

    return { sort, source, query };
  }
};
